import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { SubmitHandler, useForm, ValidationMode } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { userAccountSchema } from '../helpers/validationSchema'
import { ConfirmDeleteModal } from '../modals/ConfirmDeleteModal'
import { signOut } from '../services/auth'
import { deleteUserProfile, updateProfileInfo } from '../services/userProfile'
import { RootState, useAppDispatch } from '../store'
import { getCurrentUser } from '../store/actions/authActions'
import { signOutUser } from '../store/slice/auth'
import { AvatarFile } from '../components/AvatarFile'

export const MyAccount = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const {
    user: { data: user },
    loading,
  } = useSelector((state: RootState) => state.userReducer)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const methods = useForm<UserIdentity>({
    resolver: yupResolver(userAccountSchema),
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
    },
    mode: 'all' as keyof ValidationMode,
  })

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isDirty },
  } = methods

  const onSubmit: SubmitHandler<UserIdentity> = async (data) => {
    // Not update if there is nothing changed
    if (!isDirty) {
      return
    }

    await updateProfileInfo(data)
    await dispatch(getCurrentUser())
    reset(data)
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    setShowDeleteModal(false)
    const response = await deleteUserProfile()
    setDeleteLoading(false)
    toast.success(t('deleteAccountMessage'))

    if (response.success) {
      signOut()
      dispatch(signOutUser())
      navigate('/')
    }
  }

  // Get user avatar
  const avatarUrl = user?.avatar ? `${user.avatar}?${new Date().getTime()}` : ''

  console.log(user)

  return (
    <>
      <div
        className="mt-6 flex flex-col overflow-auto p-6 sm:top-0 sm:ml-[100px]"
        style={{ height: 'calc(100vh - 89px)' }}
      >
        <div className="mx-auto">
          <h1 className="mb-4 text-left text-[18px] font-medium">
            {t('myAccount')}
          </h1>

          <form
            className="mb-9 flex max-w-[350px] flex-col"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={`flex w-full items-center justify-center mb-6`}>
              <label
                className="flex h-[100px] w-[100px] flex-col
          items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2]"
              >
                <div className="pointer-events-none flex flex-col items-center justify-center">
                  <span className="text-5xl text-secondary">{user?.username.slice(0, 1).toUpperCase()}</span>
                </div>
              </label>
            </div>
            <Input
              variant={errors.email ? 'danger' : 'primary'}
              placeholder={t('emailPlaceholder')}
              type="text"
              helperText={errors.email?.message}
              {...register('email')}
            />
            <Input
              variant={errors.username ? 'danger' : 'primary'}
              helperText={errors.username?.message}
              placeholder={t('usernamePlaceholder')}
              type="text"
              {...register('username')}
            />
            <Button
              text={t('save')}
              type="submit"
              className="mt-1.5 h-[40px] w-full capitalize text-white"
              loading={loading}
            />
          </form>

          <h2 className="mb-2 text-left text-[18px] font-medium">
            {t('deleteForever')}
          </h2>
          <p className="mb-4 text-sm">{t('deleteAccountDescription')}</p>

          <Button
            variant="danger"
            text="Delete account"
            type="submit"
            className="h-[40px] w-full max-w-[350px] hover:bg-red-600 hover:text-white"
            loading={deleteLoading}
            onClick={() => setShowDeleteModal(true)}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        showModal={showDeleteModal}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />
    </>
  )
}
